import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from "@angular/core";
import { Router } from "@angular/router";
import { DateTime } from "luxon";
import { forkJoin, Observable } from "rxjs";
import {
  MetersGraphStats,
  MetricElement,
} from "src/app/core/models/global-stats.model";
import { User } from "src/app/core/models/user.model";
import { GlobalStatsService } from "src/app/core/services/global-stats.service";
import { FiltersStore } from "src/app/core/stores/filters.store";
import { UserStore } from "src/app/core/stores/user.store";
import { GraphGrouping } from "src/app/shared/types/hydrao-types";
import { UtilsService } from "../../shared/services/utils.service";
import {
  MeterSignalElement,
  MeterSignalsMap,
} from "../models/meter-signal.model";
import { MeterUsage } from "../models/meter-usage.model";
import {
  featuresByType,
  Meter,
  MeterData,
  MeterStates,
  MeterType,
  premiumFeaturesByType,
} from "../models/meter.model";
import { StatsElement, StatsMap } from "../models/placestats.model";
import { OrderByDirection, OrderByItem } from "../models/search-result.model";
import { MeterUsageService } from "../services/meter-usage.service";
import { MeterService } from "../services/meter.service";
import { AppStore, MeterTourScenario, PlaceTourScenario } from "./app.store";
import { AuthStore } from "./auth.store";
import { GatewayStore } from "./gateway.store";
import { PlacesStore } from "./places.store";

enum MeterScreenMode {
  Normal = "normal",
  Loading = "loading",
}

const LOG_TAG = "[METER-STORE] ";

// @Injectable()
@Injectable({
  providedIn: "root",
})
export class MeterStore {
  protected utilsService: UtilsService = inject(UtilsService);
  protected globalStatsService: GlobalStatsService = inject(GlobalStatsService);
  protected meterService: MeterService = inject(MeterService);
  protected meterUsageService: MeterUsageService = inject(MeterUsageService);
  protected authStore: AuthStore = inject(AuthStore);
  protected filtersStore: FiltersStore = inject(FiltersStore);
  protected userStore: UserStore = inject(UserStore);
  protected appStore: AppStore = inject(AppStore);
  protected placesStore: PlacesStore = inject(PlacesStore);
  protected gatewayStore: GatewayStore = inject(GatewayStore);
  private router: Router = inject(Router);

  private screenMode: MeterScreenMode = MeterScreenMode.Normal;

  // Define signals for the store state
  private readonly forceRefresh = signal<boolean>(false);
  private readonly error = signal<string | null>(null);
  readonly error$ = this.error.asReadonly();

  // ---------------------------
  // --- touring
  // ---------------------------

  readonly touring = {
    welcomeStep: {
      url: "meters/stats",
      title: $localize`:@@meters.touring.welcomeTitle:Welcome to meters section`,
      text: $localize`:@@meters.touring.welcomeText:You will learn how to manage your meters and track your water consumption. Let's start with consumption tracking.`,
    },
    placeFilterStep: {
      url: "meters/stats",
      title: $localize`:@@meters.touring.metersByPlaceTitle:Meters organized by place`,
      text: $localize`:@@meters.touring.metersByPlaceText:You can group your devices by location to make navigation easier. This filter allows you to display only the devices of the selected location.`,
    },
    periodFilterStep: {
      url: "meters/stats",
      title: $localize`:@@meters.touring.periodSelectionTitle:Choice of the period`,
      text: $localize`:@@meters.touring.periodSelectionText:Whether for the entire year or the details of a single day, feel free to explore the possibilities.`,
    },
    meterFilterStep: {
      url: "meters/stats",
      title: $localize`:@@meters.touring.yourMetersTitle:Your meters`,
      text: $localize`:@@meters.touring.yourMetersText:Select the meter(s) whose consumption you want to display. Some more detailed information are only visible if a single meter is selected.`,
    },
    statsStep: {
      url: "meters/stats",
      title: $localize`:@@meters.touring.consumptionTitle:Your consumption`,
      text: $localize`:@@meters.touring.consumptionText:You can find your consumption history based on the filters seen previously. To see more details, select a bar on the chart by clicking on it.`,
    },
    pageHeaderNavStep: {
      url: "meters/stats",
      title: $localize`:@@meters.touring.changePageTitle:Let’s go to device page`,
      text: $localize`:@@meters.touring.changePageText:To change the page, simply click on the title and select 'Devices'.`,
    },
    devicesStep: {
      url: "meters/devices",
      title: $localize`:@@meters.touring.metersManagementTitle:Management of your meters`,
      text: $localize`:@@meters.touring.metersManagementText:You can see the list of your meters and sub-meters, view their information, rename them and add new ones.`,
    },
    meterAddOrUpdateStep: {
      url: "/meters/devices",
      title: $localize`:@@meters.touring.modifyMeterTitle:Easily edit your meters`,
      text: $localize`:@@meters.touring.modifyMeterText:Simply click on the + to add a meter or sub-meter. Clicking on an existing meter allows you to rename it, move it or remove it from your account.`,
    },
  };

  readonly tourSteps$ = computed(() => {
    const tourSteps = [];

    Object.entries(this.touring).forEach(([step, data]) => {
      tourSteps.push(step + "@" + data.url);
    });

    return tourSteps;
  });

  getTourTitle(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.title : "";
  }

  getTourText(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.text : "";
  }

  // ---------------------------
  // --- page header
  // ---------------------------

  readonly headers$ = computed(() => {
    const headers = [];

    headers.push({
      id: "consumption",
      label: $localize`Consumption tracking`,
      url: "/meters",
    });

    headers.push({
      id: "equipments",
      label: $localize`Devices`,
      url: "/meters/devices",
    });

    return headers;
  });

  // ---------------------------
  // --- meters
  // ---------------------------

  private tourAddId: number = 90;
  private readonly metersLastFetch = signal<Date>(null);
  private metersRefreshDelay: number = 3600; // 1 hour

  private readonly meters = signal<Meter[]>([]);
  private tourMeters: Meter[] = [];
  private readonly metersLoading = signal<boolean>(false);
  readonly metersLoading$ = computed(() => {
    return this.metersLastFetch() === null || this.metersLoading() === true;
  });

  private readonly metersDataById = signal<Record<string, MeterData>>({});

  private readonly metersById = signal<Record<string, Meter>>({});
  private tourMetersById: Record<string, Meter> = {};
  readonly tourMetersById$ = computed(() => {
    return this.tourMetersById;
  });

  readonly metersByPlaceId$ = computed(() => {
    const metersById = this.metersById$();

    if (!metersById || Object.keys(metersById).length === 0) return {};

    const result: Record<number, Meter[]> = {};

    Object.values(metersById).forEach((meter) => {
      let placeId = meter.place_id;
      if (typeof meter.place_id !== "number") {
        placeId = -1;
      }

      if (!Array.isArray(result[placeId])) {
        result[placeId] = [];
      }
      result[placeId].push(meter);
    });

    return result;
  });

  readonly metersByGatewayId$ = computed(() => {
    const metersById = this.metersById$();

    if (!metersById || Object.keys(metersById).length === 0) return {};

    const result: Record<string, Meter[]> = {};

    Object.values(metersById).forEach((meter) => {
      if (
        typeof meter.gateway_uuid === "string" &&
        meter.gateway_uuid.length > 0
      ) {
        if (!Array.isArray(result[meter.gateway_uuid])) {
          result[meter.gateway_uuid] = [];
        }
        result[meter.gateway_uuid].push(meter);
      }
    });

    return result;
  });

  readonly _metersById$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourMetersById : this.metersById();
  });

  readonly metersById$ = computed(() => {
    const metersById = this._metersById$();
    const metersDataById = this.metersDataById();

    const result: Record<string, Meter> = {};

    Object.entries(metersById).forEach(([meterUuid, meter]) => {
      const finalMeter = meter.clone();
      const data = metersDataById[meter.device_uuid];
      if (data) {
        finalMeter.setData(data);
      }

      result[meter.device_uuid] = finalMeter;
    });

    return result;
  });

  readonly _meters$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourMeters : this.meters();
  });

  readonly meters$ = computed(() => {
    const meters = this._meters$();
    const data = this.metersDataById();

    if (meters.length === 0) return meters;

    return this._compureMetersWithData(meters, data);
  });

  readonly meterUuids$ = computed(() => {
    const metersById = this.metersById$();
    return Object.keys(metersById);
  });

  readonly metersCount$ = computed(() => {
    const metersUuids = this.meterUuids$();

    return metersUuids.length;
  });

  readonly noMeters$ = computed(() => {
    const metersLoading = this.metersLoading$();
    const meters = this.meters$();

    return metersLoading === false ? meters.length === 0 : false;
  });

  fetchMeters(forceRefresh: boolean = false) {
    try {
      if (this.metersLoading()) return;

      if (
        forceRefresh ||
        this.metersLastFetch() === null ||
        this.utilsService.calculateElapsedSeconds(this.metersLastFetch()) >
          this.metersRefreshDelay
      ) {
        this.metersLoading.set(true);
        console.log(LOG_TAG + "fetchMeters...");

        this.meterService.getMetersForStore().subscribe({
          next: (meters: Meter[]) => {
            this.meters.set(meters);
            this.metersById.set(this._computeIndexedMeters(meters));

            if (this.screenMode !== MeterScreenMode.Loading) {
              this.metersLoading.set(false);
            }

            this.metersLastFetch.set(new Date());
            if (forceRefresh === true) {
              this.needRefreshSelected = true;
            }
          },
          error: (err) => {
            console.error("failed to fetch meters : ", err);
            this.error.set(err.message || "An error occurred.");
            this.metersLoading.set(false);
          },
        });
      }
    } catch (error) {
      console.error("ERROR ON FETCH METERS", error);
    }
  }

  getFirstSeen(meterUuids: string[]): DateTime {
    let result: DateTime = null;

    const meters = this.metersById$();
    if (!meters || Object.keys(meters).length === 0) return result;

    // compute firstSeen from meters
    meterUuids.map((meterUuid) => {
      const meter: Meter = meters[meterUuid];
      // console.log("GET FIRST SEEN", meter.label, meter.first_seen, result);

      if (
        meter &&
        meter.first_seen !== null &&
        (!result || result > meter.first_seen)
      ) {
        result = meter.first_seen;
      }
    });

    return result;
  }

  getLastSeen(meterUuids: string[]): DateTime {
    let result = null;

    const meters = this.metersById$();
    if (!meters || Object.keys(meters).length === 0) return result;

    // compute lastSeen from meters
    meterUuids.map((meterUuid) => {
      const meter: Meter = meters[meterUuid];
      // console.log("GET LAST SEEN", meter.label, meter.last_seen, result);

      if (
        meter &&
        meter.last_seen !== null &&
        (!result || result < meter.last_seen)
      ) {
        result = meter.last_seen;
      }
    });

    return result;
  }

  getFirstLevelMetersFromList(meterUuids: string[]): Meter[] {
    if (!Array.isArray(meterUuids) || meterUuids.length === 0) return [];

    const metersById = this.metersById$();

    const ancestorUuids = [];
    meterUuids.map((meterUuid) => {
      const meter = metersById[meterUuid];
      if (!meter) return;

      let ancestorUuid = meter.device_uuid;
      if (meter.ancestorIds.length > 0) {
        ancestorUuid = meter.ancestorIds[0];
      }

      if (ancestorUuids.indexOf(ancestorUuid) === -1) {
        ancestorUuids.push(ancestorUuid);
      }
    });

    const meters: Meter[] = [];
    ancestorUuids.map((ancestorUuid) => {
      const meter = metersById[ancestorUuid];
      if (!meter) return;

      meters.push(meter);
    });

    return meters;
  }

  // ---------------------------
  // --- meter actions
  // ---------------------------

  private needRefreshSelected: boolean = false;

  private readonly adding = signal<boolean>(false);
  readonly adding$ = this.adding.asReadonly();
  private readonly addResult = signal<Meter | string | null>(null);
  readonly addResult$ = this.addResult.asReadonly();

  addMeter(serial: string, label: string, parentId?: string, placeId?: number) {
    if (this.adding()) return;

    this.addResult.set(null);
    this.adding.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // check serial not already in store
      let exists = Object.entries(this.tourMetersById).filter(
        ([meterUuid, meter]) => meter.serial === serial
      );
      if (exists && exists.length > 0) {
        this.addResult.set("already_registered");
      }

      // prepare new meter
      const addedMeter = this._createMeter(
        "" + this.tourAddId,
        label,
        placeId,
        null,
        null,
        null
      );
      addedMeter.serial = serial;

      // add meter to parent
      if (parentId) {
        const parentMeter = this.tourMetersById[parentId];
        if (!parentMeter) {
          this.addResult.set("parent_not_found");
        }
        this._addChildMeter(parentMeter, addedMeter);
      }

      // update stores
      this.tourMetersById[addedMeter.device_uuid] = addedMeter;
      this.tourAddId++;

      this.adding.set(false);
      this.addResult.set(addedMeter);
    } else {
      this.meterService
        .addMeterForStore(serial, label, parentId, placeId)
        .subscribe({
          next: (meter: Meter) => {
            this.adding.set(false);
            this.addResult.set(meter);
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to add meter : ", err);
            this.addResult.set(err.error_code || "unknown");
            this.adding.set(false);
          },
        });
    }
  }

  declareAddSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh items
      this.fetchMeters(true);
    }
    this.addResult.set(null);
  }

  resetAddResult() {
    this.addResult.set(null);
  }

  private readonly updating = signal<boolean>(false);
  readonly updating$ = this.updating.asReadonly();
  private readonly updateResult = signal<Meter | string | null>(null);
  readonly updateResult$ = this.updateResult.asReadonly();

  updateMeter(
    meterUuid: string,
    label: string,
    parentId?: string,
    moveChildren?: boolean,
    placeId?: number
  ) {
    if (this.updating()) return;

    this.updateResult.set(null);
    this.updating.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // get meter
      const updatedMeter = this.metersById[meterUuid];

      // update meter props
      updatedMeter.label = label;
      updatedMeter.place_id = placeId;

      const oldParentId = updatedMeter.parent_id;
      updatedMeter.parent_id = parentId;
      //TODO adapt parent & children
      // if (parentId) {
      //   const parentMeter = this.tourMetersById[parentId];
      //   if (!parentMeter) {
      //     this.updateResult.set("parent_not_found");
      //   }
      //   this._addChildMeter(parentMeter, addedMeter);
      // }

      this.updating.set(false);
      this.updateResult.set(updatedMeter);
    } else {
      this.meterService
        .updateMeterForStore(meterUuid, label, parentId, moveChildren, placeId)
        .subscribe({
          next: (meter: Meter) => {
            this.updating.set(false);
            this.updateResult.set(meter);
          },
          error: (err) => {
            console.error("failed to update meter : ", err);
            this.updateResult.set(err.error_code || "unknown");
            this.updating.set(false);
          },
        });
    }
  }

  declareUpdateSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh items
      this.fetchMeters(true);
    }
    this.updateResult.set(null);
  }

  resetUpdateResult() {
    this.updateResult.set(null);
  }

  private readonly removing = signal<boolean>(false);
  readonly removing$ = this.removing.asReadonly();
  private readonly removeResult = signal<boolean | string | null>(null);
  readonly removeResult$ = this.removeResult.asReadonly();

  removeMeter(meterUuid) {
    if (this.removing()) return;

    this.removeResult.set(null);
    this.removing.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // check meterUuid  in store
      let exists = Object.keys(this.tourMetersById).filter(
        (uuid) => uuid === meterUuid
      );
      if (!exists || exists.length === 0) {
        this.removeResult.set("unauthorized");
      }

      // remove meter
      const meter = this.tourMetersById[meterUuid];
      const parentMeterId = meter.parent_id;
      if (parentMeterId) {
        const parentMeter = this.tourMetersById[parentMeterId];
        this._removeChildMeter(parentMeter, meter);
        delete this.tourMetersById[meter.device_uuid];
      } else if (Array.isArray(meter.children) && meter.children.length > 0) {
        meter.children.map((child) => {
          child.parent_id = null;
          this.tourMeters.push(child);
        });
        this.tourMetersById = this._computeIndexedMeters(this.tourMeters);
      }

      this.removing.set(false);
      this.removeResult.set(true);
    } else {
      this.meterService.removeMeterForStore(meterUuid).subscribe({
        next: (meter: Meter) => {
          this.removing.set(false);
          this.removeResult.set(true);
        },
        error: (err) => {
          console.error("failed to remove meter : ", err);
          this.removeResult.set(err.error_code || "unknown");
          this.removing.set(false);
        },
      });
    }
  }

  declareRemoveSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh items
      this.fetchMeters(true);
    }
    this.removeResult.set(null);
  }

  resetRemoveResult() {
    this.removeResult.set(null);
  }

  // ---------------------------
  // --- sorting
  // ---------------------------

  private metersOrderByTimer: any;

  private readonly metersOrderBy = signal<string>("label-asc");
  readonly metersOrderBy$ = this.metersOrderBy.asReadonly();

  readonly sortsById$ = computed(() => {
    const sorts: OrderByItem[] = [
      {
        id: "label-asc",
        field: "label",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.aToZ:(A > Z)`,
      },
      {
        id: "label-desc",
        field: "label",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.zToA:(Z > A)`,
      },
      {
        id: "volume-asc",
        field: "volume",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.volume:Sorted on volume` + "\n" + "(- > +)",
      },
      {
        id: "volume-desc",
        field: "volume",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.volume:Sorted on volume` + "\n" + "(+ > -)",
      },
      {
        id: "lastseen-asc",
        field: "last_seen",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.comm:Sorted on last comm.` +
          "\n" +
          $localize`:@@global.sort.oldToNew:(oldest > newest)`,
      },
      {
        id: "lastseen-desc",
        field: "last_seen",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.comm:Sorted on last comm.` +
          "\n" +
          $localize`:@@global.sort.newToOld:(newest > oldest)`,
      },
    ];

    const sortItems = {};
    sorts.map((sort) => {
      sortItems[sort.id] = sort;
    });

    return sortItems;
  });

  readonly sorts$ = computed(() => {
    const sortsById = this.sortsById$();

    return Object.values(sortsById);
  });

  readonly currentSort$ = computed(() => {
    const sortsById = this.sortsById$();
    const orderBy = this.metersOrderBy$();

    if (!orderBy) return null;

    return sortsById[orderBy];
  });

  updateMetersOrderBy(newOrderBy: string): void {
    if (newOrderBy !== this.metersOrderBy()) {
      if (this.metersOrderByTimer) {
        clearTimeout(this.metersOrderByTimer);
      }

      this.metersOrderByTimer = setTimeout(() => {
        this.metersOrderBy.set(newOrderBy);
      }, 100);
    }
  }

  // ---------------------------
  // --- filtering
  // ---------------------------

  private metersSearchTimer: any;

  private readonly metersSearch = signal<string>(null);
  readonly metersSearch$ = this.metersSearch.asReadonly();

  updateMetersSearch(newSearch: string): void {
    if (newSearch !== this.metersSearch()) {
      if (this.metersSearchTimer) {
        clearTimeout(this.metersSearchTimer);
      }

      this.metersSearchTimer = setTimeout(() => {
        this.metersSearch.set(newSearch);
      }, 100);
    }
  }

  readonly placeMeters$ = computed(() => {
    const placeId = this.filtersStore.placeId$();
    const metersById = this.metersById$();

    return this.getMetersFromPlaceId(placeId);
  });

  readonly placeFlatMeters$ = computed(() => {
    const placeId = this.filtersStore.placeId$();
    const metersById = this.metersById$();

    return this.getFlatMetersFromPlaceId(placeId);
  });

  readonly placeMeterUuids$ = computed(() => {
    const placeMeters = this.placeMeters$();

    return placeMeters.map((meter) => meter.device_uuid);
  });

  readonly deepPlaceMeterUuids$ = computed(() => {
    const placeId = this.filtersStore.placeId$();
    const placesById = this.placesStore.placesById$();
    const metersById = this.metersById$();
    const metersCount = this.metersCount$();

    if (metersCount === 0) return [];

    const uuids = [];
    if (placeId === -1) {
      Object.values(metersById).forEach((meter) => {
        if (meter.place_id === null) {
          if (uuids.indexOf(meter.device_uuid) < 0) {
            uuids.push(meter.device_uuid);
          }
        }
      });
      return uuids;
    } else if (placeId !== null) {
      const place = placesById[placeId];
      Object.values(metersById).forEach((meter) => {
        if (
          meter.place_id !== null &&
          (place.id === meter.place_id ||
            place.deepChildrenIds.indexOf(meter.place_id) >= 0)
        ) {
          if (uuids.indexOf(meter.device_uuid) < 0) {
            uuids.push(meter.device_uuid);
          }
        }
      });
      return uuids;
    } else {
      return Object.keys(metersById);
    }
  });

  readonly deepPlaceMetersCount$ = computed(() => {
    const deepPlaceMeterUuids = this.deepPlaceMeterUuids$();
    const metersCount = this.metersCount$();

    if (metersCount === 0) return 0;

    return deepPlaceMeterUuids.length;
  });

  readonly filteredMeters$ = computed(() => {
    const metersById = this.metersById$();
    let filteredMeters = this.placeMeters$();
    const search = this.metersSearch();
    const stats = this.metersStats$();
    const currentSort = this.currentSort$();
    const user = this.userStore.user$();

    if (filteredMeters.length === 0) return filteredMeters;

    filteredMeters = filteredMeters.map((meter) => meter.clone());

    // filter on search
    const fixedSearch =
      typeof search === "string" && search.trim().length > 0
        ? search.trim().toLowerCase()
        : null;
    if (fixedSearch) {
      filteredMeters = filteredMeters.filter(
        (meter) =>
          meter.serial?.trim().toLowerCase().indexOf(fixedSearch) >= 0 ||
          meter.label?.trim().toLowerCase().indexOf(fixedSearch) >= 0 ||
          (user.canViewPlaces() === true &&
            meter.placeLabel &&
            meter.placeLabel.trim().toLowerCase().indexOf(fixedSearch) >= 0)
      );
      if (filteredMeters.length === 0) return filteredMeters;
    }

    // apply order by
    if (currentSort) {
      let meterUuids = filteredMeters.map((meter) => meter.device_uuid);
      let orderedMeterUuids = [];
      let notOrderedMeterUuids = [...meterUuids];
      if (currentSort.field === "volume") {
        let sortedUuids;
        // get order for meters with stats
        if (currentSort.direction === OrderByDirection.desc) {
          sortedUuids = Object.keys(stats).sort(
            (keyA, keyB) => stats[keyB].volume - stats[keyA].volume
          );
        } else {
          sortedUuids = Object.keys(stats).sort(
            (keyA, keyB) => stats[keyA].volume - stats[keyB].volume
          );
        }
        sortedUuids = sortedUuids.filter(
          (meterUuid) => meterUuids.indexOf(meterUuid) >= 0
        );
        // prepare meters without stats
        sortedUuids.map((meterUuid) => {
          if (notOrderedMeterUuids.indexOf(meterUuid) >= 0) {
            notOrderedMeterUuids.splice(
              notOrderedMeterUuids.indexOf(meterUuid),
              1
            );
          }
        });
        // build ordered meters uuid list from order direction
        if (currentSort.direction === OrderByDirection.asc) {
          orderedMeterUuids = [...notOrderedMeterUuids, ...sortedUuids];
        } else {
          orderedMeterUuids = [...sortedUuids, ...notOrderedMeterUuids];
        }
        if (orderedMeterUuids.length > 0) {
          // build ordered meters list
          const orderedMeters = [];
          orderedMeterUuids.map((meterUuid) => {
            orderedMeters.push(metersById[meterUuid]);
          });
          filteredMeters = orderedMeters;
        }
      } else if (currentSort.field === "label") {
        if (currentSort.direction === OrderByDirection.asc) {
          filteredMeters = filteredMeters.sort((meterA, meterB) =>
            meterA.universalLabel.localeCompare(meterB.universalLabel)
          );
        } else {
          filteredMeters = filteredMeters.sort((meterA, meterB) =>
            meterB.universalLabel.localeCompare(meterA.universalLabel)
          );
        }
      } else if (currentSort.field === "last_seen") {
        // console.log("SORT BY LAST_SEEN", currentSort);
        if (currentSort.direction === OrderByDirection.asc) {
          filteredMeters = filteredMeters.sort(
            (meterA, meterB) =>
              meterB.lastSeenDuration - meterA.lastSeenDuration
          );
        } else {
          filteredMeters = filteredMeters.sort(
            (meterA, meterB) =>
              meterA.lastSeenDuration - meterB.lastSeenDuration
          );
        }
      }
    }

    // console.log(LOG_TAG + "filtered meters", filteredMeters);

    return filteredMeters;
  });

  // ---------------------------
  // --- selection
  // ---------------------------

  private metersSelectedTimer: any;
  private meterUrlTimer: any;

  private readonly metersSelected = signal<string[]>([]);
  readonly metersSelected$ = this.metersSelected.asReadonly();

  private readonly unselectRecently = signal<boolean>(false);
  readonly unselectRecently$ = this.unselectRecently.asReadonly();

  readonly hasIncoherentSelection$ = computed(() => {
    const placeMeterUuids = this.placeMeterUuids$();
    const selectedUuids = this.metersSelected$();
    const metersById = this.metersById$();

    if (placeMeterUuids.length === 0 || selectedUuids.length === 0)
      return false;

    // prepare "wrong uuid list" (descendants of place root meters)
    const placeMeterDescendantUuids = [];
    placeMeterUuids.map((uuid) => {
      const placeMeter = metersById[uuid];
      if (!placeMeter) return;

      if (
        Array.isArray(placeMeter.deepChildrenIds) &&
        placeMeter.deepChildrenIds.length > 0
      ) {
        placeMeter.deepChildrenIds.map((childrenUuid) => {
          if (placeMeterDescendantUuids.indexOf(childrenUuid) < 0) {
            placeMeterDescendantUuids.push(childrenUuid);
          }
        });
      }
    });

    // check selected
    const wrongSelectedUuids = selectedUuids.filter((selectedUuid) => {
      const selectedMeter = metersById[selectedUuid];

      if (!selectedMeter) return false;
      return (
        selectedMeter.parent_id !== null &&
        selectedUuids.indexOf(selectedMeter.parent_id) >= 0 &&
        placeMeterDescendantUuids.indexOf(selectedUuid) >= 0
      );
    });

    return wrongSelectedUuids.length > 0;
  });

  readonly meter$ = computed(() => {
    const metersById = this.metersById$();
    const deepPlaceMeterUuids = this.deepPlaceMeterUuids$();
    const metersSelected = this.metersSelected();
    if (
      metersSelected.length !== 1 ||
      Object.keys(metersById).length === 0 ||
      deepPlaceMeterUuids.indexOf(metersSelected[0]) < 0
    )
      return null;

    return metersById[metersSelected[0]];
  });

  updateMetersSelected(meter_uuids: string[]) {
    //TODO vérifier si meter_uuids a changé

    if (this.metersSelectedTimer) {
      clearTimeout(this.metersSelectedTimer);
    }

    this.metersSelectedTimer = setTimeout(() => {
      this.metersSelected.set(meter_uuids);
    }, 100);
  }

  selectMeter(meter_uuid: string) {
    if (this.metersSelected().indexOf(meter_uuid) < 0) {
      const selected = [...this.metersSelected()];
      selected.push(meter_uuid);
      this.metersSelected.set(selected);
    }
  }

  selectNone() {
    if (this.metersSelected().length > 0) {
      this.metersSelected.set([]);
      this.unselectRecently.set(true);
    }
  }

  unselectMeter(meter_uuid: string) {
    if (this.metersSelected().indexOf(meter_uuid) >= 0) {
      const selected = this.metersSelected().filter(
        (device_uuid) => device_uuid !== meter_uuid
      );
      this.unselectRecently.set(true);
      this.metersSelected.set(selected);
    }
  }

  resetUnselectRecently() {
    if (this.unselectRecently() === true) {
      this.unselectRecently.set(false);
    }
  }

  async updateMeterToShowInUrl(meterUuid: string): Promise<void> {
    if (this.meterUrlTimer) {
      clearTimeout(this.meterUrlTimer);
    }

    this.meterUrlTimer = setTimeout(async () => {
      await this.router.navigate(
        [meterUuid ? "/meters/stats/" + meterUuid : "/meters/stats"],
        {
          replaceUrl: true,
          // queryParams: {
          //   selected: meterUuid,
          // },
          queryParamsHandling: "merge",
        }
      );
    }, 400);
  }

  // ---------------------------
  // --- navigation
  // ---------------------------

  private readonly meterStates = signal<MeterStates>({}); // meters with children / opening state
  readonly meterStates$ = this.meterStates.asReadonly();

  // Getter status signals for external access
  readonly loading$ = computed(() => {
    const metersCount = this.metersCount$();
    const metersLoading = this.metersLoading$();
    const metricsLoading = this.metricsLoading();
    const statsLoading = this.statsLoading();

    if (metersLoading === false) {
      return metersCount > 0 ? metricsLoading || statsLoading : false;
    } else {
      return metersLoading;
    }
  });

  toggleMeterState(meter_uuid: string) {
    const states = this.meterStates();
    states[meter_uuid] = !states[meter_uuid];
    this.meterStates.set(states);
  }

  createDataFromUuid(meter_uuid: string) {
    const metersById = this.metersById$();
    return metersById[meter_uuid] ? metersById[meter_uuid] : null;
  }

  // ---------------------------
  // --- stats
  // ---------------------------

  private statsContext: string;
  private statsLastFetch: Date;
  private statsRefreshDelay: number = 600; // 10 minutes
  private refreshTimer: any;
  private statsNbDevicesLimit = 50; // retreive stats by batch of N devices

  private readonly statsLoading = signal<boolean>(false);
  readonly statsLoading$ = this.statsLoading.asReadonly();
  private readonly statsById = signal<MeterSignalsMap>({});
  private tourStatsById = signal<MeterSignalsMap>({});

  private readonly metersStats = signal<StatsMap>({});
  private tourMeterStats = signal<StatsMap>({});

  private readonly metrics = signal<MetricElement[]>([]);
  private readonly metricsLoading = signal<boolean>(false);
  readonly metricsLoading$ = this.metricsLoading.asReadonly();

  readonly statsById$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourStatsById() : this.statsById();
  });
  readonly metersStats$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourMeterStats() : this.metersStats();
  });

  fetchStats(
    user: User,
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping
  ): void {
    try {
      if (this.statsLoading()) return;
      const meterUuids = this.meterUuids$();

      this.statsLoading.set(true);

      console.log(LOG_TAG + "fetchStats...");

      const chunkSize = this.statsNbDevicesLimit;
      const numberOfChunks = Math.ceil(meterUuids.length / chunkSize);
      const requests = Array.from({ length: numberOfChunks }, (_, index) => {
        const offset = index * chunkSize;
        return this.fetchStatsBatch(
          fromDate,
          toDate,
          graphGrouping,
          chunkSize,
          offset
        );
      });

      forkJoin(requests).subscribe({
        next: (statsArray) => {
          const stats: MeterSignalsMap = {};

          // compute statsArray to stats
          for (const partial of statsArray) {
            for (const key in partial) {
              if (!stats[key]) {
                stats[key] = [];
              }
              stats[key] = stats[key].concat(partial[key]);
            }
          }

          // store new stats
          console.log(LOG_TAG + "NEW STATS : ", stats);
          this.statsById.set(stats);
          if (this.screenMode !== MeterScreenMode.Loading) {
            this.statsLoading.set(false);
          }
          this.statsLastFetch = new Date();
        },
        error: (err) => {
          console.error("failed to fetch stats : ", err);
          this.error.set(err.message || "An error occurred.");
          this.statsLoading.set(false);
        },
        complete: () => {},
      });
    } catch (error) {
      console.error("ERROR ON FETCH STATS", error);
    }
  }

  fetchStatsBatch(
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping,
    limit: number,
    offset: number
  ): Observable<MeterSignalsMap> {
    return this.globalStatsService.getMeterStatsForStore(
      fromDate,
      toDate,
      graphGrouping,
      null,
      limit,
      offset
    );
  }

  refreshStats(
    user: User,
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping,
    uuids: string[],
    tourMode: boolean = false,
    forceRefresh: boolean = false
  ): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    this.refreshTimer = setTimeout(async () => {
      const newContext: string[] = [];
      newContext.push(`fromDate=${fromDate.toISO()}`);
      newContext.push(`toDate=${toDate.toISO()}`);
      newContext.push(`graphGrouping=${graphGrouping}`);
      newContext.push(`userId=${user.id}`);
      newContext.push(`uuids=${uuids.join(",")}`);

      if (
        forceRefresh ||
        this.statsContext !== newContext.join(",") ||
        !this.statsLastFetch ||
        this.utilsService.calculateElapsedSeconds(this.statsLastFetch) >
          this.statsRefreshDelay
      ) {
        // console.log(LOG_TAG +"context changed !!!");

        if (tourMode) {
          console.log(LOG_TAG + "REFRESH STATS TOUR DATA");
          const tourStats = this._prepareTourStats(
            fromDate,
            toDate,
            graphGrouping
          );
          this.tourStatsById.set(tourStats);
        } else {
          console.log(LOG_TAG + "REFRESH STATS DATA");

          // refresh data
          this.fetchStats(user, fromDate, toDate, graphGrouping);
          this.statsContext = newContext.join(",");
        }
      }
    }, 100);
  }

  // getMeterStats(meterUuid: string): StatsElement {
  //   const stats = this.metersStats$();

  //   return Object.keys(stats).indexOf(meterUuid) >= 0 ? stats[meterUuid] : null;
  // }

  getAllMetrics(meterUuids: string[]): MetersGraphStats {
    const result: MetersGraphStats = {
      meter_volume: 0,
    };

    let statsById = this.statsById$();
    if (statsById === null || Object.keys(statsById).length === 0) return null;

    // compute metrics from stats
    meterUuids.map((meterUuid) => {
      const stats: MeterSignalElement[] = statsById[meterUuid];
      if (!stats || stats.length === 0) return;

      stats.map((stat) => {
        result.meter_volume += stat.volume || 0;
      });
    });

    return result;
  }

  // ---------------------------
  // --- raw stats
  // ---------------------------

  private refreshRawTimer: any;

  private readonly rawStatsLoading = signal<boolean>(false);
  readonly rawStatsLoading$ = this.rawStatsLoading.asReadonly();
  private readonly meterRawStats = signal<MeterSignalElement[]>([]);
  private tourRawStats = signal<MeterSignalElement[]>([]);

  readonly meterRawStats$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourRawStats() : this.meterRawStats();
  });

  fetchRawStats(meterUuid: string, fromDate: DateTime, toDate: DateTime): void {
    try {
      if (this.rawStatsLoading()) return;

      this.rawStatsLoading.set(true);

      console.log(LOG_TAG + "fetchRawStats...");

      this.globalStatsService
        .getMeterStatsForStore(fromDate, toDate, GraphGrouping.raw, meterUuid)
        .subscribe({
          next: (stats: MeterSignalsMap) => {
            console.log(LOG_TAG + "new raw stats : ", stats);
            this.meterRawStats.set(stats[meterUuid]);
            if (this.screenMode !== MeterScreenMode.Loading) {
              this.rawStatsLoading.set(false);
            }
          },
          error: (err) => {
            console.error("failed to fetch raw stats : ", err);
            this.error.set(err.message || "An error occurred.");
            this.rawStatsLoading.set(false);
          },
        });
    } catch (error) {
      console.error("ERROR ON FETCH RAW STATS", error);
    }
  }

  refreshRawStats(
    meterUuid: string,
    fromDate: DateTime,
    toDate: DateTime,
    tourMode: boolean
  ) {
    if (this.refreshRawTimer) {
      clearTimeout(this.refreshRawTimer);
    }

    this.refreshRawTimer = setTimeout(async () => {
      if (tourMode) {
        console.log(LOG_TAG + "REFRESH RAW STATS TOUR DATA");
        this.tourRawStats.set(
          this.generateMeterStats(fromDate, toDate, GraphGrouping.raw)
        );
      } else {
        console.log(LOG_TAG + "REFRESH RAW STATS");
        this.fetchRawStats(meterUuid, fromDate, toDate);
      }
    });
  }

  // ---------------------------
  // --- usage stats
  // ---------------------------

  private refreshUsageTimer: any;

  private readonly usageStatsLoading = signal<boolean>(false);
  readonly usageStatsLoading$ = this.usageStatsLoading.asReadonly();

  private readonly meterUsageStats = signal<MeterUsage>(null);
  private tourUsageStats = signal<MeterUsage>(null);

  readonly meterUsageStats$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourUsageStats() : this.meterUsageStats();
  });

  fetchUsageStats(
    meterUuid: string,
    fromDate: DateTime,
    toDate: DateTime
  ): void {
    try {
      if (this.usageStatsLoading()) return;

      this.usageStatsLoading.set(true);

      console.log(LOG_TAG + "fetchUsageStats...");

      this.meterUsageService
        .getUsageForStore(meterUuid, fromDate, toDate)
        .subscribe({
          next: (stats: MeterUsage) => {
            console.log(LOG_TAG + "new usage stats : ", stats);
            this.meterUsageStats.set(stats);
            if (this.screenMode !== MeterScreenMode.Loading) {
              this.usageStatsLoading.set(false);
            }
          },
          error: (err) => {
            console.error("failed to fetch usage stats : ", err);
            this.error.set(err.message || "An error occurred.");
            this.usageStatsLoading.set(false);
          },
        });
    } catch (error) {
      console.error("ERROR ON FETCH USAGE STATS", error);
    }
  }

  refreshUsageStats(
    meterUuid: string,
    fromDate: DateTime,
    toDate: DateTime,
    tourMode: boolean
  ) {
    if (this.refreshUsageTimer) {
      clearTimeout(this.refreshUsageTimer);
    }

    this.refreshUsageTimer = setTimeout(async () => {
      if (tourMode) {
        console.log(LOG_TAG + "REFRESH USAGE STATS TOUR DATA");
        this.tourUsageStats.set(
          this._generateMeterUsageStats(fromDate, toDate)
        );
      } else {
        console.log(LOG_TAG + "REFRESH USAGE STATS");
        this.fetchUsageStats(meterUuid, fromDate, toDate);
      }
    }, 100);
  }

  // ---------------------------
  // --- dashboard stats
  // ---------------------------

  private last30dStatsLastFetch: Date;
  private last30dStatsRefreshDelay: number = 600; // 10 minutes

  private readonly last30dStatsLoading = signal<boolean>(false);
  readonly last30dStatsLoading$ = this.last30dStatsLoading.asReadonly();
  private readonly last30dStatsById = signal<MeterSignalsMap>({});
  private tourLast30dStatsById = signal<MeterSignalsMap>({});

  private readonly metersLast30dStats = signal<StatsMap>({});
  private tourMeterLast30dStats = signal<StatsMap>({});

  readonly last30dStatsById$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourLast30dStatsById() : this.last30dStatsById();
  });
  readonly metersLast30dStats$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourMeterLast30dStats() : this.metersLast30dStats();
  });
  readonly todayActiveMeterUuids$ = computed(() => {
    // const last30dStatsById = this.last30dStatsById$();

    // return this._computeActiveMetersFromLast30dStats(last30dStatsById);
    const metersById = this.metersById$();

    const activeUuids = [];

    const now = DateTime.local();
    Object.entries(metersById).forEach(([uuid, meter]) => {
      if (meter.last_seen !== null && meter.last_seen.hasSame(now, "day")) {
        if (activeUuids.indexOf(uuid) < 0) {
          activeUuids.push(uuid);
        }
      }
    });

    return activeUuids;
  });

  fetchLast30dStats(forced: boolean = false): void {
    try {
      if (this.last30dStatsLoading()) return;

      const tourMode = untracked(this.appStore.tourMode$);

      const toDate = DateTime.utc().set({
        hour: 23,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      const fromDate = toDate.minus({ days: 30 });
      const graphGrouping = GraphGrouping.days;

      if (
        forced ||
        !this.last30dStatsLastFetch ||
        this.utilsService.calculateElapsedSeconds(this.last30dStatsLastFetch) >
          this.last30dStatsRefreshDelay
      ) {
        if (tourMode) {
          const tourStats = this._prepareTourStats(
            fromDate,
            toDate,
            graphGrouping
          );
          this.tourLast30dStatsById.set(tourStats);
          this.tourMeterLast30dStats.set(this._computeMeterStats(tourStats));
        } else {
          this.last30dStatsLoading.set(true);

          console.log(LOG_TAG + "fetchLast30dStats...");

          console.log(
            LOG_TAG + "fetchLast30dStats",
            fromDate,
            toDate,
            GraphGrouping
          );

          this.globalStatsService
            .getMeterStatsForStore(fromDate, toDate, graphGrouping)
            .subscribe({
              next: (stats: MeterSignalsMap) => {
                console.log(
                  LOG_TAG + "fetchLast30dStats / NEW LAST 30D STATS : ",
                  stats
                );
                this.last30dStatsById.set(stats);
                this.metersLast30dStats.set(this._computeMeterStats(stats));
                if (this.screenMode !== MeterScreenMode.Loading) {
                  this.last30dStatsLoading.set(false);
                }
                this.last30dStatsLastFetch = new Date();
              },
              error: (err) => {
                console.error("failed to fetch last 30d stats : ", err);
                this.error.set(err.message || "An error occurred.");
                this.last30dStatsLoading.set(false);
              },
            });
        }
      }
    } catch (error) {
      console.error("ERROR ON FETCH LAST 30D STATS", error);
    }
  }

  getMeterLast30dStats(meterUuid: string): StatsElement {
    const stats = this.metersLast30dStats$();

    return Object.keys(stats).indexOf(meterUuid) >= 0 ? stats[meterUuid] : null;
  }

  getPlaceLast30dStats(placeId: number): number {
    let result = 0;
    const meters = this.getMetersFromPlaceId(placeId);

    meters.map((meter) => {
      const stats: StatsElement = this.getMeterLast30dStats(meter.device_uuid);

      if (stats && stats.volume > 0) {
        result += stats.volume;
      }
    });

    return result;
  }

  getPlaceTodayStats(placeId: number): number {
    let result = 0;
    const meters = this.getMetersFromPlaceId(placeId);
    const last30dStatsById = this.last30dStatsById$();

    meters.map((meter) => {
      const meterStats: MeterSignalElement[] =
        last30dStatsById[meter.device_uuid];

      if (Array.isArray(meterStats) && meterStats.length > 0) {
        const lastDayStats: MeterSignalElement =
          meterStats[meterStats.length - 1];

        if (lastDayStats.date.hasSame(DateTime.local(), "day")) {
          result += lastDayStats.volume;
        }
      }
    });

    return result;
  }

  // ---------------------------
  // --- LIFE CYCLE METHODS
  // ---------------------------

  public constructor() {
    this._prepareTourData();

    effect(
      () => {
        const metersById = this._metersById$();

        this.unselectRecently.set(false);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const metersById = this.metersById$();

        if (this.needRefreshSelected === true) {
          if (this.metersSelected$().length > 1) {
            const placeRootMeters = this.getMetersFromPlaceId(
              this.filtersStore.placeId$()
            );
            const placeRootMeterUuids = placeRootMeters.map(
              (placeMeter) => placeMeter.device_uuid
            );
            this.metersSelected.set(placeRootMeterUuids);
          }
          this.needRefreshSelected = false;
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const statsById = this.statsById$();
        const tourMode = untracked(this.appStore.tourMode$);
        const user = this.userStore.user$();

        if (tourMode) {
          this.tourMeterStats.set(this._computeMeterStats(statsById, user));
        } else {
          this.metersStats.set(this._computeMeterStats(statsById, user));
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const metersById = this._metersById$();
        const gatewaysById = this.gatewayStore.gatewaysById$();
        const placesById = this.placesStore.placesById$();

        const metersDataById: Record<string, MeterData> = {};

        if (Object.keys(metersById).length === 0) {
          this.metersDataById.set(metersDataById);
          return;
        }

        console.log(
          LOG_TAG + "EFFECT refresh meters data from gateways & places",
          metersById
        );

        Object.entries(metersById).map(([uuid, meter]) => {
          const data: MeterData = {
            placeLabel: null,
            gatewayLabel: null,
          };

          if (meter.gateway_uuid) {
            data.gatewayLabel = this.gatewayStore.getLabel(meter.gateway_uuid);
          }

          if (meter.place_id) {
            data.placeLabel = this.placesStore.getLabel(meter.place_id);
          }

          metersDataById[meter.device_uuid] = data;
        });

        this.metersDataById.set(metersDataById);
        console.log(
          LOG_TAG + "EFFECT refresh meters data from gateways & places => ",
          metersDataById
        );
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const logged = this.authStore.logged$();
        const fromDate = this.filtersStore.dateFrom$();
        const toDate = this.filtersStore.dateTo$();
        const graphGrouping = this.filtersStore.graphGrouping$();
        const user = this.userStore.user$();
        const meterUuids = this.meterUuids$();
        const meter = this.meter$();

        const tourMode = untracked(this.appStore.tourMode$);
        const metersSelected = this.metersSelected();
        const forcedRefresh = this.forceRefresh();

        if (!logged) return;

        let nbMeters = 0;
        if (Array.isArray(meterUuids) && meterUuids.length > 0) {
          nbMeters = meterUuids.length;
        }

        if (
          user &&
          nbMeters > 0 &&
          fromDate &&
          toDate &&
          graphGrouping !== GraphGrouping.raw
        ) {
          console.log(LOG_TAG + "EFFECT => refresh stats from filters");

          this.refreshStats(
            user,
            fromDate,
            toDate,
            graphGrouping,
            meterUuids,
            tourMode,
            forcedRefresh
          );

          if (meter && meter.canViewMeterUsage) {
            this.refreshUsageStats(
              meter.device_uuid,
              fromDate,
              toDate,
              tourMode
            );
          }
        } else if (
          nbMeters > 0 &&
          graphGrouping === GraphGrouping.raw &&
          meter &&
          meter.canViewRawSignal
        ) {
          console.log(
            LOG_TAG + "EFFECT => refresh raw meter signals here ????"
          );
          this.refreshRawStats(metersSelected[0], fromDate, toDate, tourMode);
        }

        // if (!tourMode) {
        // refresh meters if necessary
        this.fetchMeters(forcedRefresh);

        if (forcedRefresh === true) {
          this.forceRefresh.set(false);
        }
        // }
      },
      { allowSignalWrites: true }
    );
  }

  getFlatMetersFromPlaceId(placeId: number): Meter[] {
    const placeRootMeters = this.getMetersFromPlaceId(placeId);

    if (placeRootMeters.length === 0) {
      return placeRootMeters;
    }

    const placeMeters = [...placeRootMeters];
    placeRootMeters.map((meter) => {
      if (meter.deepChildrenIds.length === 0) {
        return;
      }

      meter.deepChildrenIds.map((childMeterId) => {
        const childMeter = this.metersById$()[childMeterId];
        if (childMeter) {
          placeMeters.push(childMeter);
        }
      });
    });

    return placeMeters;
  }

  getMetersFromPlaceId(placeId: number): Meter[] {
    if (placeId === null) {
      return this.meters$().map((meter) => meter.clone());
    } else {
      let metersByPlaceId = this.metersByPlaceId$();

      // first : meters directly linked to placeId
      let directMeters = metersByPlaceId[placeId]
        ? metersByPlaceId[placeId]
        : [];
      const uuidsToExclude = new Set([]);
      directMeters.forEach((meter) => {
        meter.deepChildrenIds.forEach((childUuid) => {
          if (!uuidsToExclude.has(childUuid)) {
            uuidsToExclude.add(childUuid);
          }
        });
      });
      // console.log("DIRECT METERS / EXCLUDE", uuidsToExclude);
      directMeters = directMeters.filter((meter) => {
        if (!uuidsToExclude.has(meter.device_uuid)) {
          return meter;
        }
      });
      let meterUuids = directMeters.map((meter) => meter.device_uuid);
      // console.log(LOG_TAG + "DIRECT METERS", directMeters);

      // second : meters linked to descendant places of placeId
      let indirectMeters = [];
      const placeDescendantIds =
        this.placesStore.getPlaceDescendantIds(placeId);
      // console.log(
      //   "INDIRECT METERS / DESCENDANTS PLACE IDS",
      //   placeDescendantIds
      // );
      placeDescendantIds.forEach((subPlaceId) => {
        const subPlaceMeters = metersByPlaceId[subPlaceId]
          ? metersByPlaceId[subPlaceId]
          : [];
        // console.log(
        //   "INDIRECT METERS / METERS FOR DESCENDANTS PLACE ID " + subPlaceId,
        //   subPlaceMeters
        // );

        subPlaceMeters.forEach((subPlaceMeter) => {
          const meterSet = new Set(meterUuids);
          const hasAlreadyAncestor = subPlaceMeter.ancestorIds.some((id) =>
            meterSet.has(id)
          );
          if (
            (subPlaceMeter.parent_id === null || !hasAlreadyAncestor) &&
            meterUuids.indexOf(subPlaceMeter.device_uuid) < 0
          ) {
            indirectMeters.push(subPlaceMeter);
            meterUuids.push(subPlaceMeter.device_uuid);
          }
        });
      });
      // console.log(LOG_TAG + "INDIRECT METERS", indirectMeters);

      // ...directMeters, ...indirectMeters
      const meters = [];
      directMeters.map((directMeter) => {
        meters.push(directMeter.clone());
      });
      indirectMeters.map((indirectMeter) => {
        meters.push(indirectMeter.clone());
      });
      return meters;
    }
  }

  triggerForcedRefresh(): void {
    this.forceRefresh.set(true);
  }

  getGlobalGraphData(
    meterUuids: string[],
    dateFrom: DateTime,
    dateTo: DateTime,
    grouping: GraphGrouping
  ): MeterSignalElement[] {
    const result: MeterSignalElement[] = [];

    const statsById = this.statsById$();
    if (
      statsById === null ||
      Object.keys(statsById).length === 0 ||
      grouping === GraphGrouping.raw
    )
      return result;

    let addQty = null;
    let compareQty = null;
    switch (grouping) {
      case GraphGrouping.months:
        addQty = "months";
        compareQty = "month";
        break;
      case GraphGrouping.days:
        addQty = "days";
        compareQty = "day";
        break;
      case GraphGrouping.hours:
        addQty = "hours";
        compareQty = "hour";
        break;
    }
    // console.log(LOG_TAG + "getGlobalGraphData unit : ", grouping, compareQty);

    // consolidate stats for defined meters for each time bar
    for (
      let barDate: DateTime = dateFrom;
      barDate <= dateTo;
      barDate = barDate.plus({ [addQty]: 1 })
    ) {
      let volume: number = null;

      meterUuids.map((meterUuid) => {
        const statsMeter: MeterSignalElement[] = statsById[meterUuid];

        if (!statsMeter) return;
        const dateStatsMeter = statsMeter.find((stats) =>
          stats.date.hasSame(barDate, compareQty)
        );
        if (dateStatsMeter) {
          if (volume === null) volume = 0;
          volume += dateStatsMeter.volume;
        }
      });

      result.push(
        new MeterSignalElement(
          barDate,
          null,
          volume,
          null,
          null,
          null,
          null,
          false
        )
      );
    }

    return result;
  }

  getMeterGraphData(
    meterUuid: string,
    graphGrouping: GraphGrouping
  ): MeterSignalElement[] {
    const result: MeterSignalElement[] = [];

    if (graphGrouping === GraphGrouping.raw) {
      return this.meterRawStats$();
    } else {
      const statsById = this.statsById$();
      if (statsById === null || Object.keys(statsById).length === 0)
        return result;

      return statsById[meterUuid] ? statsById[meterUuid] : [];
    }
  }

  public clear() {
    // reset local values
    this.metersLastFetch.set(null);
    this.statsLastFetch = null;
    this.statsContext = null;
    this.statsContext = null;

    // reset debounces
    clearTimeout(this.refreshTimer);
    clearTimeout(this.refreshRawTimer);
    clearTimeout(this.refreshUsageTimer);
    clearTimeout(this.metersSelectedTimer);
    clearTimeout(this.metersSearchTimer);
    clearTimeout(this.metersOrderByTimer);
    clearTimeout(this.meterUrlTimer);

    // reset states
    this.usageStatsLoading.set(false);
    this.meterUsageStats.set(null);

    this.metricsLoading.set(false);
    this.metrics.set([]);

    this.statsLoading.set(false);
    this.statsById.set({});
    this.metersStats.set({});

    this.rawStatsLoading.set(false);
    this.meterRawStats.set([]);

    this.last30dStatsLoading.set(false);
    this.last30dStatsById.set({});
    this.metersLast30dStats.set({});

    this.meters.set([]);
    this.metersById.set({});
    this.metersLoading.set(false);
    this.metersDataById.set({});

    this.adding.set(false);
    this.addResult.set(null);

    this.updating.set(false);
    this.updateResult.set(null);

    this.removing.set(false);
    this.removeResult.set(null);

    this.metersOrderBy.set("label-asc");
    this.metersSearch.set(null);
    this.metersSelected.set([]);
    this.unselectRecently.set(false);
    this.meterStates.set({});

    this.error.set(null);
  }

  // ---- PRIVATE METHODS ------

  private _computeMeterStats(
    statsById: MeterSignalsMap,
    user: User = null
  ): StatsMap {
    let result: StatsMap = {};

    Object.entries(statsById).forEach(([meterUuid, stats]) => {
      let volume: number = 0;
      let flow: number = 0;
      let temperature: number = 0;
      let count = 0;
      stats.map((stat) => {
        volume += stat.volume;
        temperature += stat.temperature;
        flow += stat.flow;
        count++;
      });

      const elem = new StatsElement(
        this.utilsService,
        parseFloat(volume.toFixed(2)),
        null,
        count > 0 ? Math.round(flow / count) : null,
        null,
        count > 0 ? Math.round(temperature / count) : null,
        null
      );
      if (user) {
        elem.updateValuesWithUnitsWithUser(user);
      }

      result[meterUuid] = elem;
    });

    return result;
  }

  private _computeIndexedMeters(meters: Meter[]): Record<string, Meter> {
    const index: Record<string, Meter> = {};

    function registerMeter(
      meter: Meter,
      placesStore: PlacesStore,
      parentAncestors: string[] = [],
      deepLevel: number = 0
    ) {
      if (!index[meter.device_uuid]) {
        meter.ancestorIds = [...parentAncestors];
        meter.deepLevel = deepLevel;
        meter.deepChildrenIds = [];
        index[meter.device_uuid] = meter;
      }

      // update childrenIds of parent
      if (meter.parent_id && index[meter.parent_id]) {
        index[meter.parent_id].childrenIds.push(meter.device_uuid);
      }

      // compute recursively chidlren
      if (meter.children) {
        for (const child of meter.children) {
          registerMeter(
            child,
            placesStore,
            [...index[meter.device_uuid].ancestorIds, meter.device_uuid],
            deepLevel + 1
          );
          index[meter.device_uuid].deepChildrenIds.push(child.device_uuid);
          index[meter.device_uuid].deepChildrenIds.push(
            ...index[child.device_uuid].deepChildrenIds
          );
        }
      }
    }

    // recursive analyze of meters
    meters.forEach((meter) => registerMeter(meter, this.placesStore));

    return index;
  }

  private _compureMetersWithData(
    meters: Meter[],
    data: Record<string, MeterData>
  ): Meter[] {
    function registerMeter(meter: Meter) {
      const meterData = data[meter.device_uuid];
      if (meterData) {
        meter.setData(meterData);
      }

      // compute recursively chidlren
      if (meter.children) {
        for (const child of meter.children) {
          registerMeter(child);
        }
      }
    }

    // recursive analyze of meters
    meters.forEach((meter) => registerMeter(meter));

    return meters;
  }

  // ---- TOUR METHODS --------

  private _prepareTourStats(
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping
  ): MeterSignalsMap {
    let tourStats: MeterSignalsMap = {};

    Object.entries(this.tourMetersById).forEach(([meterUuid, meter]) => {
      tourStats[meterUuid] = this.generateMeterStats(
        fromDate,
        toDate,
        graphGrouping
      );
    });

    return tourStats;
  }

  private _generateMeterUsageStats(fromDate: DateTime, toDate: DateTime) {
    let nbDays = Math.floor(Math.abs(toDate.diff(fromDate, "days").days));
    if (nbDays === 0) {
      nbDays = 1;
    }

    const stats: StatsMap = this.tourMeterStats();
    const totalVolume: number = stats["volume"] ? stats["volume"].volume : 0;

    return new MeterUsage(
      totalVolume * 0.05,
      0,
      totalVolume * 0.55,
      totalVolume * 0.18,
      0,
      totalVolume * 0.12,
      totalVolume * 0.1,
      0
    );
  }

  public generateMeterStats(
    fromDate: DateTime,
    toDate: DateTime,
    graphGrouping: GraphGrouping
  ): MeterSignalElement[] {
    const result: MeterSignalElement[] = [];

    let maxDayVolume = 30;
    let nbDays = Math.floor(Math.abs(toDate.diff(fromDate, "days").days));
    if (nbDays === 0) {
      nbDays = 1;
      maxDayVolume = 20;
    }

    let maxBars = 30;
    switch (graphGrouping) {
      case GraphGrouping.days:
        maxBars = 30;
        break;
      case GraphGrouping.months:
        maxBars = 12;
        break;
      case GraphGrouping.hours:
        maxBars = 24;
        break;
      case GraphGrouping.raw:
        maxBars = 60; //TODO que mettre ici / 60 mins dans 1 heure ?
        break;
    }
    const nbBars = Math.floor(Math.random() * maxBars);

    for (let barIndex = 0; barIndex < nbBars; barIndex++) {
      let barDate: DateTime = toDate.minus({ days: nbBars - barIndex });
      switch (graphGrouping) {
        case GraphGrouping.days:
          barDate = fromDate.plus({ days: barIndex });
          break;
        case GraphGrouping.months:
          barDate = fromDate.plus({ months: barIndex });
          break;
        case GraphGrouping.hours:
          barDate = fromDate.plus({ hours: barIndex });
          break;
        case GraphGrouping.raw:
          //TODO que mettre ici ???
          barDate = fromDate.plus({ minutes: barIndex });
          break;
      }

      const dayVolume = Math.floor(Math.random() * maxDayVolume);
      const barVolume = dayVolume * nbDays;
      const hasCalculatedVolume = Math.floor(Math.random() * 2);
      const dayCalculatedVolume = Math.floor(
        Math.random() * (maxDayVolume / 2)
      );
      const barCalculatedVolume = (dayCalculatedVolume * nbDays) / 2;

      // entre 20 et 28
      const barTemperature =
        dayVolume > 0
          ? parseFloat((Math.random() * (28 - 20) + 20).toFixed(2))
          : 0;

      // entre 0 et 4.5 bar
      const barFlow =
        dayVolume > 0 ? parseFloat((Math.random() * 4.5).toFixed(2)) : 0;

      // entre 100 et 1000 => grenoble entre 250 et 350
      const barConductivity =
        dayVolume > 0
          ? parseFloat((Math.random() * (350 - 250) + 250).toFixed(2))
          : 0;

      result.push(
        new MeterSignalElement(
          barDate,
          0, //TODO duration ???
          barVolume,
          hasCalculatedVolume === 1 ? barCalculatedVolume : 0,
          barFlow,
          barTemperature,
          barConductivity,
          false
        )
      );
    }

    return result;
  }

  private _prepareUnplacedMeters(): void {
    // meters without place
    const generalData = ["01", "General", null];
    const homeData = ["02", "Home", null];
    const gardenData = ["03", "Garden", null];

    const general = this._createMeter(
      generalData[0],
      generalData[1],
      parseInt(generalData[2]),
      DateTime.local().minus({ minutes: 3 }),
      MeterType.drac_mid,
      true,
      "00005813d3093eb7"
    );

    const home = this._createMeter(
      homeData[0],
      homeData[1],
      parseInt(homeData[2]),
      DateTime.local().minus({ month: 1 }),
      MeterType.drac_mid,
      true,
      "00005813d3093eb7"
    );

    const garden = this._createMeter(
      gardenData[0],
      gardenData[1],
      parseInt(gardenData[2]),
      DateTime.local().minus({ minutes: 30 }),
      MeterType.drac_mid,
      true,
      "00005813d3093eb7"
    );

    this._addChildMeter(general, home);
    this._addChildMeter(general, garden);
    this.tourMeters.push(general);
  }

  private _prepareGrenobleMeters(placesScenario: PlaceTourScenario) {
    const grenoblePlaceId = "1";
    // const grenobleCampingPlaceId = 2;
    let grenobleCampingSwimmingPoolPlaceId = "3";
    let grenobleCampingPremiumZonePlaceId = "4";
    if (placesScenario === PlaceTourScenario.One) {
      grenobleCampingSwimmingPoolPlaceId = grenoblePlaceId;
      grenobleCampingPremiumZonePlaceId = grenoblePlaceId;
    }

    const officeData = ["21", "OfficeG", grenoblePlaceId];
    const swimmingPoolData = [
      "22",
      "SwimmingPoolG",
      grenobleCampingSwimmingPoolPlaceId,
    ];
    const premiumData = ["23", "PremiumG", grenobleCampingPremiumZonePlaceId];

    const office = this._createMeter(
      officeData[0],
      officeData[1],
      parseInt(officeData[2]),
      DateTime.local().minus({ minutes: 45 }),
      MeterType.drac_mid,
      null
    );
    this.tourMeters.push(office);

    const swimmingPool = this._createMeter(
      swimmingPoolData[0],
      swimmingPoolData[1],
      parseInt(swimmingPoolData[2]),
      DateTime.local().minus({ months: 5 }),
      MeterType.drac_mid,
      null
    );
    this.tourMeters.push(swimmingPool);

    const premiumZone = this._createMeter(
      premiumData[0],
      premiumData[1],
      parseInt(premiumData[2]),
      DateTime.local().minus({ seconds: 10 }),
      MeterType.drac_mid,
      null
    );
    this.tourMeters.push(premiumZone);
  }

  private _prepareParisMeters() {
    const parisPlaceId = "100";
    const parisBat1PlaceId = "101";
    const parisBat2PlaceId = "102";
    const parisBat2BuildingPlaceId = "103";
    const parisBat2BuildingFloorPlaceId = "104";

    const generalP1Data = ["51", "GeneralP1", parisPlaceId];
    const generalP2Data = ["52", "GeneralP2", parisPlaceId];
    const localTechP1Data = ["53", "LocalTechP1", parisBat1PlaceId];
    const parcP2Data = ["54", "ParcP2", parisBat2PlaceId];
    const rdcP1Data = ["55", "RdcP1", parisBat1PlaceId];
    const floorP1Data = ["56", "FloorP1", parisBat1PlaceId];
    const floorEastP1Data = ["57", "FloorEastP1", parisBat1PlaceId];
    const floorWestP1Data = ["58", "FloorWestP1", parisBat1PlaceId];
    const guestP1Data = ["59", "GuestP1", parisBat1PlaceId];
    const localTechP2Data = [
      "60",
      "LocalTechP2",
      parisBat2BuildingFloorPlaceId,
    ];
    const centralP2Data = ["61", "CentralP2", parisBat2BuildingPlaceId];
    const floorlP2Data = ["62", "FloorP2", parisBat2BuildingFloorPlaceId];

    // paris root meters

    // ---- generalP1
    const generalP1 = this._createMeter(
      generalP1Data[0],
      generalP1Data[1],
      parseInt(generalP1Data[2]),
      DateTime.local().minus({ seconds: 8 }),
      MeterType.drac_mid,
      true
    );

    const rdcP1 = this._createMeter(
      rdcP1Data[0],
      rdcP1Data[1],
      parseInt(rdcP1Data[2]),
      DateTime.local().minus({ days: 28 }),
      MeterType.drac_mid,
      true
    );

    const floorP1 = this._createMeter(
      floorP1Data[0],
      floorP1Data[1],
      parseInt(floorP1Data[2]),
      DateTime.local().minus({ months: 2 }),
      MeterType.drac_mid,
      true
    );

    const floorEastP1 = this._createMeter(
      floorEastP1Data[0],
      floorEastP1Data[1],
      parseInt(floorEastP1Data[2]),
      DateTime.local().minus({ days: 2 }),
      MeterType.drac_mid,
      true
    );

    const floorWestP1 = this._createMeter(
      floorWestP1Data[0],
      floorWestP1Data[1],
      parseInt(floorWestP1Data[2]),
      DateTime.local().minus({ seconds: 36 }),
      MeterType.drac_mid,
      true
    );

    const guestP1 = this._createMeter(
      guestP1Data[0],
      guestP1Data[1],
      parseInt(guestP1Data[2]),
      DateTime.local().minus({ minutes: 16 }),
      MeterType.drac_mid,
      true
    );

    this._addChildMeter(floorWestP1, guestP1);
    this._addChildMeter(floorP1, floorEastP1);
    this._addChildMeter(floorP1, floorWestP1);
    this._addChildMeter(generalP1, rdcP1);
    this._addChildMeter(generalP1, floorP1);
    this.tourMeters.push(generalP1);

    // ---- generalP2
    const generalP2 = this._createMeter(
      generalP2Data[0],
      generalP2Data[1],
      parseInt(generalP2Data[2]),
      DateTime.local().minus({ seconds: 8 }),
      MeterType.drac_mid,
      true
    );

    const localTechP2 = this._createMeter(
      localTechP2Data[0],
      localTechP2Data[1],
      parseInt(localTechP2Data[2]),
      DateTime.local().minus({ days: 18 }),
      MeterType.drac_mid,
      true
    );

    const centralP2 = this._createMeter(
      centralP2Data[0],
      centralP2Data[1],
      parseInt(centralP2Data[2]),
      DateTime.local().minus({ months: 6 }),
      MeterType.drac_mid,
      true
    );

    const floorP2 = this._createMeter(
      floorlP2Data[0],
      floorlP2Data[1],
      parseInt(floorlP2Data[2]),
      DateTime.local().minus({ seconds: 28 }),
      MeterType.drac_mid,
      true
    );

    this._addChildMeter(centralP2, floorP2);
    this._addChildMeter(generalP2, localTechP2);
    this._addChildMeter(generalP2, centralP2);
    this.tourMeters.push(generalP2);

    // bat1 root meters
    const localTechP1 = this._createMeter(
      localTechP1Data[0],
      localTechP1Data[1],
      parseInt(localTechP1Data[2]),
      DateTime.local().minus({ days: 2 }),
      MeterType.drac_mid,
      true
    );
    this.tourMeters.push(localTechP1);

    // bat2 root meters
    const parcP2 = this._createMeter(
      parcP2Data[0],
      parcP2Data[1],
      parseInt(parcP2Data[2]),
      DateTime.local().minus({ days: 8 }),
      MeterType.drac_mid,
      true
    );
    this.tourMeters.push(parcP2);
  }

  private _prepareMonoMeter() {
    const home = this._createMeter(
      "01",
      "Home",
      null,
      DateTime.local().minus({ seconds: 8 }),
      MeterType.drac_mid,
      false,
      "008b4fcc92ca122baa8659b584d180b7"
    );
    this.tourMeters.push(home);
  }

  private _prepareMetersTest(): void {
    const drac1 = this._createMeter(
      "01",
      "Home",
      null,
      DateTime.local().minus({ seconds: 8 }),
      MeterType.drac,
      false,
      "00005813d3093eb1"
    );
    const drac2 = this._createMeter(
      "02",
      "Corridor 1st floor",
      3,
      DateTime.local().minus({ minutes: 10 }),
      MeterType.drac,
      false,
      "00005813d3093eb2"
    );
    const drac3 = this._createMeter(
      "03",
      "Hall 1st floor (premium)",
      3,
      DateTime.local().minus({ minutes: 2 }),
      MeterType.drac,
      true,
      "00005813d3093eb2"
    );
    const drac4 = this._createMeter(
      "04",
      "Garden (premium)",
      null,
      DateTime.local().minus({ days: 8 }),
      MeterType.drac,
      true,
      "00005813d3093eb1"
    );
    const dracSai1 = this._createMeter(
      "05",
      "Office",
      1,
      DateTime.local().minus({ second: 8 }),
      MeterType.drac_flowmeter,
      false
    );
    const dracSai2 = this._createMeter(
      "06",
      "Dressing",
      5,
      DateTime.local().minus({ months: 10 }),
      MeterType.drac_flowmeter,
      false,
      "00005813d3093eb5"
    );
    const dracSai3 = this._createMeter(
      "07",
      "Test flowmeter (premium)",
      null,
      DateTime.local().minus({ hour: 2 }),
      MeterType.drac_flowmeter,
      true
    );
    const dracSai4 = this._createMeter(
      "08",
      "Toilets 1st floor (premium)",
      3,
      DateTime.local().minus({ weeks: 1 }),
      MeterType.drac_flowmeter,
      true,
      "00005813d3093eb2"
    );
    const dracMid1 = this._createMeter(
      "09",
      "Penthouse",
      3,
      DateTime.local().minus({ hours: 8 }),
      MeterType.drac_mid,
      false,
      "00005813d3093eb2"
    );
    const dracMid2 = this._createMeter(
      "10",
      "Test mid",
      null,
      DateTime.local().minus({ months: 10 }),
      MeterType.drac_mid,
      false,
      "00005813d3093eb1"
    );
    const dracMid3 = this._createMeter(
      "11",
      "Meter 11 (premium)",
      null,
      DateTime.local().minus({ years: 2 }),
      MeterType.drac_mid,
      true
    );
    const dracMid4 = this._createMeter(
      "12",
      "Lobby 2nd floor (premium)",
      4,
      DateTime.local().minus({ second: 1 }),
      MeterType.drac_mid,
      true,
      "00005813d3093eb3"
    );
    const v2_1 = this._createMeter(
      "13",
      "Test v2",
      null,
      DateTime.local().minus({ days: 8 }),
      MeterType.v2,
      false,
      "00005813d3093eb1"
    );
    const v2_2 = this._createMeter(
      "14",
      "Laundry 1st floor",
      3,
      DateTime.local().minus({ years: 10 }),
      MeterType.v2,
      false,
      "00005813d3093eb2"
    );
    const v2_3 = this._createMeter(
      "15",
      "Lobby 1st floor (premium)",
      3,
      DateTime.local().minus({ week: 2 }),
      MeterType.v2,
      true,
      "00005813d3093eb2"
    );
    const v2_4 = this._createMeter(
      "16",
      "Corridor 1st floor (premium)",
      3,
      DateTime.local().minus({ second: 1 }),
      MeterType.v2,
      true,
      "00005813d3093eb2"
    );
    const sense1 = this._createMeter(
      "17",
      "Corridor 2nd floor",
      4,
      DateTime.local().minus({ days: 18 }),
      MeterType.sense,
      false,
      "00005813d3093eb3"
    );
    const sense2 = this._createMeter(
      "18",
      "Test sense",
      null,
      DateTime.local().minus({ years: 10 }),
      MeterType.sense,
      false,
      "00005813d3093eb1"
    );
    const sense3 = this._createMeter(
      "19",
      "Toilets 2nd floor (premium)",
      4,
      DateTime.local().minus({ week: 2 }),
      MeterType.sense,
      true,
      "00005813d3093eb3"
    );
    const sense4 = this._createMeter(
      "20",
      "Laundry 2nd floor (premium)",
      4,
      DateTime.local().minus({ second: 1 }),
      MeterType.sense,
      true,
      "00005813d3093eb3"
    );
    const maddalena1 = this._createMeter(
      "21",
      "Kitchen",
      5,
      DateTime.local().minus({ minutes: 18 }),
      MeterType.thirdparty_maddalena,
      false,
      "00005813d3093eb5"
    );
    const maddalena2 = this._createMeter(
      "22",
      "Secondary home",
      5,
      DateTime.local().minus({ months: 10 }),
      MeterType.thirdparty_maddalena,
      false,
      "00005813d3093eb5"
    );
    const hac1 = this._createMeter(
      "23",
      "Swimming pool",
      5,
      DateTime.local().minus({ second: 18 }),
      MeterType.thirdparty_hac,
      false,
      "00005813d3093eb5"
    );
    const hac2 = this._createMeter(
      "24",
      "Garden",
      5,
      DateTime.local().minus({ days: 10 }),
      MeterType.thirdparty_hac,
      false,
      "00005813d3093eb5"
    );
    const hacItron1 = this._createMeter(
      "25",
      "Room 02",
      3,
      DateTime.local().minus({ second: 18 }),
      MeterType.thirdparty_hac_itron,
      false,
      "00005813d3093eb2"
    );
    const hacItron2 = this._createMeter(
      "26",
      "Swimming pool",
      3,
      DateTime.local().minus({ days: 10 }),
      MeterType.thirdparty_hac_itron,
      false,
      "00005813d3093eb2"
    );
    const hacDiehl1 = this._createMeter(
      "27",
      "Room 03",
      3,
      DateTime.local().minus({ day: 1 }),
      MeterType.thirdparty_hac_diehl,
      false,
      "00005813d3093eb2"
    );
    const hacDiehl2 = this._createMeter(
      "28",
      "Bathtub",
      5,
      DateTime.local().minus({ week: 1 }),
      MeterType.thirdparty_hac_diehl,
      false
    );
    const hacSensus1 = this._createMeter(
      "29",
      "Test sensus",
      null,
      DateTime.local().minus({ weeks: 1 }),
      MeterType.thirdparty_hac_sensus,
      false,
      "00005813d3093eb1"
    );
    const hacSensus2 = this._createMeter(
      "30",
      "Parking",
      5,
      DateTime.local().minus({ hour: 1 }),
      MeterType.thirdparty_hac_sensus,
      false,
      "00005813d3093eb2"
    );
    const adeunis1 = this._createMeter(
      "31",
      "Test adeunis",
      5,
      DateTime.local().minus({ second: 1 }),
      MeterType.thirdparty_adeunis,
      false,
      "00005813d3093eb5"
    );
    const adeunis2 = this._createMeter(
      "32",
      "Bathroom",
      null,
      DateTime.local().minus({ second: 185 }),
      MeterType.thirdparty_adeunis,
      false,
      "00005813d3093eb1"
    );
    const quandify1 = this._createMeter(
      "33",
      "Test quandify",
      null,
      DateTime.local().minus({ hours: 3 }),
      MeterType.thirdparty_adeunis,
      false,
      "00005813d3093eb1"
    );
    const quandify2 = this._createMeter(
      "34",
      "Toilets",
      5,
      DateTime.local().minus({ minute: 310 }),
      MeterType.thirdparty_adeunis,
      false,
      "00005813d3093eb5"
    );

    this._addChildMeter(dracMid4, sense3);
    this._addChildMeter(dracMid4, sense4);

    this._addChildMeter(drac1, drac4);
    this._addChildMeter(drac1, hacSensus1);
    this._addChildMeter(hacSensus1, adeunis2);
    this._addChildMeter(drac1, dracMid3);

    this._addChildMeter(maddalena2, hac1);
    this._addChildMeter(maddalena2, hac2);
    this._addChildMeter(maddalena2, quandify2);
    this._addChildMeter(quandify2, adeunis1);

    this._addChildMeter(drac3, dracMid1);
    this._addChildMeter(drac3, v2_3);
    this._addChildMeter(drac3, v2_4);
    this._addChildMeter(drac3, hacDiehl1);
    this._addChildMeter(hacDiehl1, dracSai4);
    this._addChildMeter(hacDiehl1, v2_2);
    this._addChildMeter(hacDiehl1, hacItron1);

    this.tourMeters.push(drac1);
    this.tourMeters.push(drac2);
    this.tourMeters.push(drac3);
    this.tourMeters.push(dracSai1);
    this.tourMeters.push(dracSai2);
    this.tourMeters.push(dracSai3);
    this.tourMeters.push(dracMid2);
    this.tourMeters.push(dracMid4);
    this.tourMeters.push(v2_1);
    this.tourMeters.push(sense1);
    this.tourMeters.push(sense2);
    this.tourMeters.push(maddalena1);
    this.tourMeters.push(maddalena2);
    this.tourMeters.push(hacItron2);
    this.tourMeters.push(hacDiehl2);
    this.tourMeters.push(hacSensus2);
    this.tourMeters.push(quandify1);
  }

  private _prepareTourData() {
    switch (this.appStore.metersScenario) {
      case MeterTourScenario.None:
        // nothing to do
        break;
      case MeterTourScenario.One:
        this._prepareMonoMeter();
        break;
      case MeterTourScenario.Full:
        switch (this.appStore.placesScenario) {
          case PlaceTourScenario.Full:
            this._prepareUnplacedMeters();
            this._prepareGrenobleMeters(this.appStore.placesScenario);
            //TODO lyon
            this._prepareParisMeters();
            break;
          case PlaceTourScenario.One:
            this._prepareUnplacedMeters();
            this._prepareGrenobleMeters(this.appStore.placesScenario);
            break;
          case PlaceTourScenario.None:
            this._prepareUnplacedMeters();
            break;
        }
        break;
      case MeterTourScenario.Test:
        this._prepareMetersTest();
        break;
    }

    // prepare meters
    this.tourMetersById = this._computeIndexedMeters(this.tourMeters);
    // console.log(LOG_TAG + "TOUR METERS BY ID", this.tourMetersById);
  }

  private _createMeter(
    id: string,
    label: string,
    placeId: number,
    last_seen: DateTime,
    type: MeterType,
    is_premium: boolean,
    gateway_uuid?: string
  ): Meter {
    const meterFeatures = is_premium
      ? [...featuresByType[type], ...premiumFeaturesByType[type]]
      : featuresByType[type];

    return new Meter(
      "83363124050000" + id,
      DateTime.local().minus({ years: 3 }),
      last_seen,
      label,
      "JY19832" + id,
      DateTime.local(),
      placeId,
      null,
      meterFeatures,
      type,
      null,
      Math.floor(Math.random() * 100),
      null,
      gateway_uuid,
      null
    );
  }

  private _addChildMeter(parent: Meter, child: Meter) {
    if (parent.children === null) {
      parent.children = [];
    }
    child.parent_id = parent.device_uuid;
    parent.children.push(child);
  }

  private _removeChildMeter(parent: Meter, child: Meter) {
    parent.children = parent.children.filter(
      (parentChild) => parentChild.device_uuid !== child.device_uuid
    );
    if (Array.isArray(child.children) && child.children.length > 0) {
      child.children.map((childChild) => {
        this._addChildMeter(parent, childChild);
      });
    }
    child.parent_id = null;
    child.children = [];
  }
}
